import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import PageNavigation from "@/components/PageNavigation";
import SEOHead from "@/components/seo/SEOHead";
import juniorBianchim from "@/assets/junior-bianchim.jpg";
import { 
  ArrowRight, 
  Clock, 
  User, 
  Target, 
  Lightbulb, 
  Trophy,
  Star,
  Zap,
  TrendingUp,
  Users,
  Award,
  Medal,
  Crown,
  Gamepad2,
  BarChart3,
  CheckCircle,
  XCircle,
  Brain,
  Heart,
  Rocket,
  Gift,
  Flag,
  Timer,
  ChevronRight,
  BookOpen,
  Flame,
  Shield,
  Target as TargetIcon
} from "lucide-react";
import { useNavigate } from "react-router-dom";

const GamificacaoVendas = () => {
  const navigate = useNavigate();

  // Elementos de gamificação
  const gamificationElements = [
    {
      icon: Trophy,
      title: "Pontos",
      description: "Unidade básica de recompensa que quantifica o desempenho. Cada ação positiva gera pontos que podem ser acumulados e trocados.",
      example: "10 pontos por ligação, 50 por reunião, 200 por venda fechada",
      benefit: "Feedback imediato e tangível do progresso"
    },
    {
      icon: Medal,
      title: "Badges/Medalhas",
      description: "Conquistas visuais que reconhecem marcos específicos. Funcionam como troféus digitais que mostram expertise.",
      example: '"Mestre das Objeções", "Fechador do Mês", "100 Ligações"',
      benefit: "Status e reconhecimento social perante a equipe"
    },
    {
      icon: BarChart3,
      title: "Rankings/Leaderboards",
      description: "Classificações que mostram a posição de cada vendedor em relação aos colegas. Geram competição saudável.",
      example: "Top 10 semanal, ranking mensal, campeonato trimestral",
      benefit: "Motivação por comparação social e competitividade"
    },
    {
      icon: Flag,
      title: "Níveis/Progressão",
      description: "Sistema de evolução onde vendedores sobem de nível conforme acumulam experiência e resultados.",
      example: "Nível 1: Iniciante → Nível 5: Expert → Nível 10: Lenda",
      benefit: "Sensação de crescimento e jornada de desenvolvimento"
    },
    {
      icon: Gift,
      title: "Recompensas",
      description: "Prêmios tangíveis ou intangíveis que podem ser conquistados através do acúmulo de pontos ou conquistas.",
      example: "Day-off, vouchers, viagens, equipamentos, cursos",
      benefit: "Incentivo material que complementa a motivação intrínseca"
    },
    {
      icon: Target,
      title: "Desafios/Missões",
      description: "Objetivos específicos com prazo definido que estimulam comportamentos desejados.",
      example: '"Faça 20 cold calls hoje", "Feche 3 vendas esta semana"',
      benefit: "Foco em objetivos claros e alcançáveis"
    }
  ];

  // Psicologia por trás da gamificação
  const psychologyPrinciples = [
    {
      icon: Brain,
      title: "Dopamina e Recompensa",
      description: "Cada conquista, ponto ou badge libera dopamina no cérebro, o mesmo neurotransmissor associado ao prazer. Isso cria um ciclo de reforço positivo que faz o vendedor querer repetir o comportamento.",
      application: "Micro-recompensas frequentes são mais eficazes que grandes prêmios esporádicos"
    },
    {
      icon: Heart,
      title: "Motivação Intrínseca vs Extrínseca",
      description: "A gamificação bem feita transforma motivação extrínseca (prêmios) em intrínseca (prazer pela atividade). O vendedor passa a gostar do processo, não apenas do resultado.",
      application: "Combine recompensas tangíveis com reconhecimento social e sensação de maestria"
    },
    {
      icon: Users,
      title: "Comparação Social",
      description: "Segundo a teoria de Festinger, humanos têm necessidade inata de se comparar com outros. Rankings satisfazem essa necessidade de forma produtiva.",
      application: "Use rankings segmentados para que todos tenham chance de brilhar"
    },
    {
      icon: Rocket,
      title: "Teoria do Flow",
      description: "Mihaly Csikszentmihalyi descobriu que entramos em estado de 'flow' quando há equilíbrio entre desafio e habilidade. Gamificação cria esse equilíbrio.",
      application: "Ajuste a dificuldade dos desafios conforme o vendedor evolui"
    },
    {
      icon: Award,
      title: "Necessidade de Competência",
      description: "A teoria da autodeterminação de Deci e Ryan mostra que humanos precisam se sentir competentes. Níveis e progressão satisfazem essa necessidade.",
      application: "Crie uma jornada clara de desenvolvimento com marcos visíveis"
    },
    {
      icon: TrendingUp,
      title: "Efeito de Progresso",
      description: "Estudos mostram que quanto mais perto estamos de um objetivo, mais motivados ficamos. Barras de progresso exploram esse efeito.",
      application: "Mostre sempre o progresso em direção ao próximo nível ou meta"
    }
  ];

  // Estatísticas sobre gamificação
  const statistics = [
    { value: "89%", label: "dos funcionários são mais produtivos com gamificação", icon: TrendingUp },
    { value: "72%", label: "relatam aumento no engajamento diário", icon: Flame },
    { value: "48%", label: "de aumento médio na performance de vendas", icon: Trophy },
    { value: "36%", label: "de redução no turnover de equipes comerciais", icon: Users }
  ];

  // Tipos de rankings
  const rankingTypes = [
    {
      title: "Ranking de Performance Geral",
      description: "Mede o desempenho global do vendedor considerando múltiplas métricas ponderadas.",
      metrics: ["Vendas fechadas (40%)", "Ticket médio (25%)", "Taxa de conversão (20%)", "Atividades realizadas (15%)"],
      pros: ["Visão holística", "Evita foco em única métrica", "Justo para diferentes perfis"],
      cons: ["Mais complexo de entender", "Pode diluir especialidades"]
    },
    {
      title: "Ranking por Atividades",
      description: "Foca nas ações que levam a resultados: ligações, e-mails, reuniões, propostas.",
      metrics: ["Ligações realizadas", "E-mails enviados", "Reuniões agendadas", "Propostas enviadas"],
      pros: ["Fácil de medir", "Incentiva esforço", "Bom para iniciantes"],
      cons: ["Pode gerar quantidade sem qualidade", "Não mede resultado final"]
    },
    {
      title: "Ranking por Resultados",
      description: "Considera apenas o que foi efetivamente vendido e o valor gerado.",
      metrics: ["Receita gerada", "Número de vendas", "Novos clientes", "Upsells realizados"],
      pros: ["Foco no que importa", "Clareza total", "Alinhado com objetivos"],
      cons: ["Favorece quem tem melhores leads", "Pode gerar frustração em iniciantes"]
    },
    {
      title: "Ranking de Evolução",
      description: "Compara o vendedor consigo mesmo, medindo melhoria percentual.",
      metrics: ["% de melhoria vs mês anterior", "Crescimento de pipeline", "Evolução da taxa de conversão"],
      pros: ["Justo para todos os níveis", "Incentiva crescimento pessoal", "Menos frustrante"],
      cons: ["Quem já é bom tem menos margem", "Mais complexo de calcular"]
    }
  ];

  // Exemplos de desafios
  const challengeExamples = [
    {
      category: "Diários",
      challenges: [
        "Faça 15 ligações antes do almoço - 30 pontos",
        "Agende 2 reuniões hoje - 50 pontos",
        "Envie 5 propostas personalizadas - 40 pontos"
      ],
      icon: Timer
    },
    {
      category: "Semanais",
      challenges: [
        "Feche 3 vendas até sexta - 200 pontos + badge",
        "Mantenha taxa de conversão acima de 30% - 150 pontos",
        "Faça follow-up em todos os leads quentes - 100 pontos"
      ],
      icon: Flag
    },
    {
      category: "Mensais",
      challenges: [
        "Atinja 120% da meta - Prêmio especial",
        "Zero leads perdidos por falta de follow-up - Destaque",
        "Treine 5 horas no simulador de IA - Badge Expert"
      ],
      icon: Crown
    },
    {
      category: "Especiais",
      challenges: [
        "Primeira venda do mês - Badge 'First Blood'",
        "10 vendas em sequência sem perder - Badge 'On Fire'",
        "Venda para cliente perdido - Badge 'Ressurreição'"
      ],
      icon: Medal
    }
  ];

  // Erros comuns
  const commonMistakes = [
    {
      mistake: "Gamificar tudo indiscriminadamente",
      consequence: "Fadiga de gamificação e perda de significado",
      solution: "Selecione apenas os comportamentos mais importantes para gamificar"
    },
    {
      mistake: "Recompensas desproporcionais",
      consequence: "Desmotivação quando o esforço não vale a pena",
      solution: "Calibre recompensas com base no esforço real necessário"
    },
    {
      mistake: "Apenas rankings competitivos",
      consequence: "80% da equipe fica desmotivada por nunca ganhar",
      solution: "Combine rankings com metas pessoais e desafios colaborativos"
    },
    {
      mistake: "Ignorar quem está no meio",
      consequence: "Foco apenas no top e no bottom da equipe",
      solution: "Crie categorias e ligas para todos terem chance"
    },
    {
      mistake: "Falta de transparência nas regras",
      consequence: "Desconfiança e sensação de injustiça",
      solution: "Documente todas as regras e torne os cálculos visíveis"
    },
    {
      mistake: "Não atualizar o sistema",
      consequence: "Monotonia e previsibilidade matam a motivação",
      solution: "Renove desafios, adicione eventos especiais regularmente"
    }
  ];

  // Passos para implementação
  const implementationSteps = [
    {
      step: 1,
      title: "Diagnóstico da Equipe",
      description: "Antes de implementar qualquer sistema, entenda o perfil da sua equipe. Pessoas diferentes são motivadas por coisas diferentes.",
      actions: [
        "Aplique pesquisa de perfil motivacional",
        "Identifique o que já funciona informalmente",
        "Mapeie resistências potenciais",
        "Defina objetivos claros para a gamificação"
      ]
    },
    {
      step: 2,
      title: "Design do Sistema",
      description: "Projete o sistema de pontos, badges e rankings de forma equilibrada e alinhada com os objetivos de negócio.",
      actions: [
        "Defina quais comportamentos serão recompensados",
        "Estabeleça a tabela de pontuação",
        "Crie badges significativas e alcançáveis",
        "Determine os tipos de ranking a utilizar"
      ]
    },
    {
      step: 3,
      title: "Escolha da Ferramenta",
      description: "Selecione uma plataforma que suporte seu sistema de gamificação e se integre com suas ferramentas de vendas.",
      actions: [
        "Avalie opções de mercado (CRMs com gamificação, plataformas dedicadas)",
        "Priorize integração com ferramentas existentes",
        "Considere facilidade de uso e adoção",
        "Verifique relatórios e analytics disponíveis"
      ]
    },
    {
      step: 4,
      title: "Lançamento com Impacto",
      description: "O lançamento define o tom do programa. Faça com que seja memorável e gere engajamento inicial.",
      actions: [
        "Organize um evento de kickoff",
        "Explique todas as regras claramente",
        "Inicie com um desafio de lançamento especial",
        "Garanta suporte intensivo na primeira semana"
      ]
    },
    {
      step: 5,
      title: "Monitoramento e Ajustes",
      description: "Gamificação não é 'configure e esqueça'. Requer acompanhamento constante e evolução.",
      actions: [
        "Monitore métricas de engajamento diariamente",
        "Colete feedback da equipe regularmente",
        "Ajuste dificuldades e recompensas conforme necessário",
        "Adicione novos desafios e eventos periodicamente"
      ]
    }
  ];

  // Casos de sucesso
  const successCases = [
    {
      company: "Salesforce",
      result: "40% de aumento na adoção de CRM",
      description: "Implementou badges para completar perfis de clientes e fazer follow-ups no prazo.",
      lesson: "Gamificação pode melhorar processos além das vendas diretas"
    },
    {
      company: "Microsoft",
      result: "3,5x mais feedbacks de qualidade",
      description: "Usou pontos e rankings para incentivar vendedores a dar feedback sobre o produto.",
      lesson: "Comportamentos difíceis de medir também podem ser gamificados"
    },
    {
      company: "Autodesk",
      result: "54% de aumento em trials convertidos",
      description: "Gamificou o processo de onboarding de clientes com missões e conquistas.",
      lesson: "Gamificação funciona tanto para equipe interna quanto para clientes"
    }
  ];

  // Métricas para acompanhar
  const metricsToTrack = [
    {
      category: "Engajamento",
      metrics: ["Taxa de participação diária", "Tempo médio na plataforma", "Frequência de check-in", "Compartilhamentos de conquistas"]
    },
    {
      category: "Performance",
      metrics: ["Evolução da taxa de conversão", "Aumento do ticket médio", "Crescimento do pipeline", "Redução do ciclo de vendas"]
    },
    {
      category: "Comportamento",
      metrics: ["Atividades por vendedor/dia", "Qualidade das interações", "Adesão a processos", "Uso de técnicas treinadas"]
    },
    {
      category: "Cultura",
      metrics: ["Satisfação da equipe", "Competitividade saudável", "Colaboração entre pares", "Retenção de talentos"]
    }
  ];

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "Gamificação em Vendas: O Poder dos Pontos e Rankings",
    "description": "Descubra como implementar gamificação na sua equipe comercial para aumentar motivação, engajamento e resultados de vendas.",
    "author": {
      "@type": "Person",
      "name": "Junior Bianchim"
    },
    "publisher": {
      "@type": "Organization",
      "name": "Bianchim Business"
    },
    "datePublished": "2025-11-15",
    "dateModified": "2025-11-15"
  };

  return (
    <>
      <SEOHead 
        title="Gamificação em Vendas: O Poder dos Pontos e Rankings"
        description="Aprenda a implementar gamificação na sua equipe comercial. Guia completo com pontos, rankings, badges, psicologia e exemplos práticos para aumentar resultados."
        keywords="gamificação vendas, pontos vendas, ranking vendedores, motivação equipe comercial, badges vendas, sistema pontos vendas"
        canonicalUrl="https://bianchimbusiness.com.br/posts/gamificacao-vendas"
        ogType="article"
        articlePublishedTime="2024-12-12T00:00:00Z"
        articleAuthor="Junior Bianchim"
      />
      
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }} />

      <div className="min-h-screen bg-gradient-to-br from-background via-background to-accent/10">
        <Header />

        <main id="main-content">
          <article>
            {/* Hero Section */}
            <header className="relative py-12 sm:py-20 md:py-32 overflow-hidden">
              <div 
                className="absolute inset-0 bg-cover bg-center opacity-20"
                style={{ 
                  backgroundImage: "url('https://images.unsplash.com/photo-1511512578047-dfb367046420?w=1920&q=80')" 
                }}
                role="img"
                aria-label="Imagem de fundo representando gamificação e jogos"
              />
              <div className="absolute inset-0 bg-gradient-to-b from-background/50 via-background/80 to-background" />
              
              <div className="container mx-auto px-4 relative z-10">
                <div className="max-w-4xl mx-auto text-center">
                  <div className="flex items-center justify-center gap-4 mb-6">
                    <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-accent/50 bg-accent/10 backdrop-blur-sm">
                      <Gamepad2 className="w-4 h-4 text-accent" aria-hidden="true" />
                      <span className="text-sm font-medium text-accent">Gestão de Equipes</span>
                    </span>
                  </div>
                  
                  <h1 className="text-3xl sm:text-4xl md:text-6xl font-bold leading-tight mb-6">
                    <span className="bg-gradient-to-r from-primary via-accent to-secondary bg-clip-text text-transparent">
                      Gamificação em Vendas
                    </span>
                    <br />
                    <span className="text-foreground">O Poder dos Pontos e Rankings</span>
                  </h1>
                  
                  <p className="text-xl text-muted-foreground max-w-2xl mx-auto mb-8">
                    Transforme sua equipe comercial em uma máquina de resultados usando a psicologia dos jogos. 
                    Descubra como pontos, badges e rankings podem multiplicar sua performance.
                  </p>

                  <div className="flex flex-wrap items-center justify-center gap-6 text-sm text-muted-foreground">
                    <div className="flex items-center gap-2">
                      <User className="w-4 h-4 text-primary" aria-hidden="true" />
                      <span>Por <strong className="text-foreground">Junior Bianchim</strong></span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4 text-primary" aria-hidden="true" />
                      <time dateTime="2025-11-15">15 Nov 2025</time>
                      <span>• 15 min de leitura</span>
                    </div>
                  </div>
                </div>
              </div>
            </header>

            {/* Stats Section */}
            <section className="py-12 bg-muted/30" aria-labelledby="stats-heading">
              <div className="container mx-auto px-4">
                <h2 id="stats-heading" className="sr-only">Estatísticas sobre gamificação</h2>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                  {statistics.map((stat, index) => {
                    const Icon = stat.icon;
                    return (
                      <div key={index} className="text-center p-6 rounded-2xl bg-card border border-primary/20">
                        <Icon className="w-8 h-8 text-primary mx-auto mb-3" aria-hidden="true" />
                        <p className="text-3xl md:text-4xl font-bold text-primary mb-2">{stat.value}</p>
                        <p className="text-sm text-muted-foreground">{stat.label}</p>
                      </div>
                    );
                  })}
                </div>
              </div>
            </section>

            {/* Intro Section */}
            <section className="py-16 sm:py-20" aria-labelledby="intro-heading">
              <div className="container mx-auto px-4">
                <div className="max-w-4xl mx-auto">
                  <h2 id="intro-heading" className="text-3xl sm:text-4xl font-bold mb-8 text-center">
                    <span className="bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
                      O Que é Gamificação em Vendas?
                    </span>
                  </h2>
                  
                  <div className="prose prose-lg max-w-none">
                    <p className="text-muted-foreground text-lg leading-relaxed mb-6">
                      Gamificação é a aplicação de elementos e mecânicas de jogos em contextos que não são jogos. 
                      Em vendas, isso significa transformar o trabalho diário do vendedor em uma experiência mais 
                      envolvente, motivadora e, principalmente, <strong className="text-foreground">mais produtiva</strong>.
                    </p>
                    
                    <p className="text-muted-foreground text-lg leading-relaxed mb-6">
                      Não se trata de transformar o trabalho em brincadeira. Trata-se de usar os mesmos gatilhos 
                      psicológicos que tornam os jogos tão viciantes para <strong className="text-foreground">criar 
                      engajamento genuíno</strong> com as atividades de vendas.
                    </p>

                    <div className="grid md:grid-cols-2 gap-6 my-10">
                      <Card className="p-6 border-destructive/30 bg-destructive/5">
                        <div className="flex items-center gap-3 mb-4">
                          <XCircle className="w-6 h-6 text-destructive" />
                          <h3 className="font-bold text-lg">Sem Gamificação</h3>
                        </div>
                        <ul className="space-y-2 text-muted-foreground">
                          <li>• Vendedores desmotivados após rejeições</li>
                          <li>• Foco apenas no resultado final (venda)</li>
                          <li>• Competição tóxica ou inexistente</li>
                          <li>• Dificuldade em medir esforço</li>
                          <li>• Feedback apenas mensal ou trimestral</li>
                        </ul>
                      </Card>

                      <Card className="p-6 border-primary/30 bg-primary/5">
                        <div className="flex items-center gap-3 mb-4">
                          <CheckCircle className="w-6 h-6 text-primary" />
                          <h3 className="font-bold text-lg">Com Gamificação</h3>
                        </div>
                        <ul className="space-y-2 text-muted-foreground">
                          <li>• Cada ação gera recompensa imediata</li>
                          <li>• Valorização do processo, não só do resultado</li>
                          <li>• Competição saudável e motivadora</li>
                          <li>• Visibilidade total do desempenho</li>
                          <li>• Feedback em tempo real</li>
                        </ul>
                      </Card>
                    </div>

                    <p className="text-muted-foreground text-lg leading-relaxed">
                      A beleza da gamificação está em sua capacidade de tornar o invisível visível. Cada ligação, 
                      cada e-mail, cada follow-up passa a ter valor mensurável e reconhecido. O vendedor deixa de 
                      sentir que trabalha no vácuo e passa a ver <strong className="text-foreground">progresso 
                      tangível</strong> a cada ação.
                    </p>
                  </div>
                </div>
              </div>
            </section>

            {/* Elementos de Gamificação */}
            <section className="py-16 sm:py-20 bg-muted/30" aria-labelledby="elements-heading">
              <div className="container mx-auto px-4">
                <div className="text-center mb-12">
                  <h2 id="elements-heading" className="text-3xl sm:text-4xl font-bold mb-4">
                    <span className="bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
                      Os 6 Elementos Essenciais
                    </span>
                  </h2>
                  <p className="text-muted-foreground max-w-2xl mx-auto">
                    Conheça os pilares fundamentais de qualquer sistema de gamificação eficaz
                  </p>
                </div>

                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {gamificationElements.map((element, index) => {
                    const Icon = element.icon;
                    return (
                      <Card key={index} className="p-6 border-primary/20 hover:border-primary/50 transition-all">
                        <div className="flex items-center gap-3 mb-4">
                          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary to-accent flex items-center justify-center">
                            <Icon className="w-6 h-6 text-white" aria-hidden="true" />
                          </div>
                          <h3 className="text-xl font-bold">{element.title}</h3>
                        </div>
                        <p className="text-muted-foreground mb-4">{element.description}</p>
                        <blockquote className="border-l-4 border-accent pl-3 py-1 mb-4 bg-accent/5 rounded-r">
                          <p className="text-sm italic">{element.example}</p>
                        </blockquote>
                        <div className="flex items-start gap-2">
                          <CheckCircle className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                          <p className="text-sm text-primary">{element.benefit}</p>
                        </div>
                      </Card>
                    );
                  })}
                </div>
              </div>
            </section>

            {/* Psicologia da Gamificação */}
            <section className="py-16 sm:py-20" aria-labelledby="psychology-heading">
              <div className="container mx-auto px-4">
                <div className="text-center mb-12">
                  <h2 id="psychology-heading" className="text-3xl sm:text-4xl font-bold mb-4">
                    <span className="bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
                      A Psicologia Por Trás da Gamificação
                    </span>
                  </h2>
                  <p className="text-muted-foreground max-w-2xl mx-auto">
                    Entenda os princípios científicos que fazem a gamificação funcionar
                  </p>
                </div>

                <div className="space-y-8 max-w-5xl mx-auto">
                  {psychologyPrinciples.map((principle, index) => {
                    const Icon = principle.icon;
                    const isEven = index % 2 === 0;
                    
                    return (
                      <div 
                        key={index}
                        className={`flex flex-col ${isEven ? 'md:flex-row' : 'md:flex-row-reverse'} gap-6 items-center`}
                      >
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-4">
                            <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                              <Icon className="w-5 h-5 text-primary" aria-hidden="true" />
                            </div>
                            <h3 className="text-xl font-bold">{principle.title}</h3>
                          </div>
                          <p className="text-muted-foreground mb-4">{principle.description}</p>
                          <div className="flex items-start gap-3 p-4 rounded-lg bg-card border border-primary/20">
                            <Lightbulb className="w-5 h-5 text-accent mt-0.5 flex-shrink-0" />
                            <p className="text-sm">
                              <strong className="text-foreground">Aplicação prática:</strong>{" "}
                              <span className="text-muted-foreground">{principle.application}</span>
                            </p>
                          </div>
                        </div>
                        <div className="w-full md:w-48 h-48 rounded-2xl bg-gradient-to-br from-primary/20 via-accent/20 to-secondary/20 flex items-center justify-center">
                          <Icon className="w-20 h-20 text-primary/50" aria-hidden="true" />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </section>

            {/* Tipos de Rankings */}
            <section className="py-16 sm:py-20 bg-muted/30" aria-labelledby="rankings-heading">
              <div className="container mx-auto px-4">
                <div className="text-center mb-12">
                  <h2 id="rankings-heading" className="text-3xl sm:text-4xl font-bold mb-4">
                    <span className="bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
                      Tipos de Rankings e Quando Usar Cada Um
                    </span>
                  </h2>
                  <p className="text-muted-foreground max-w-2xl mx-auto">
                    Diferentes rankings servem a diferentes propósitos. Escolha sabiamente.
                  </p>
                </div>

                <div className="grid md:grid-cols-2 gap-8">
                  {rankingTypes.map((ranking, index) => (
                    <Card key={index} className="p-6 border-primary/20">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary to-accent flex items-center justify-center text-white font-bold">
                          {index + 1}
                        </div>
                        <h3 className="text-xl font-bold">{ranking.title}</h3>
                      </div>
                      <p className="text-muted-foreground mb-4">{ranking.description}</p>
                      
                      <div className="mb-4">
                        <p className="text-sm font-semibold mb-2">Métricas consideradas:</p>
                        <div className="flex flex-wrap gap-2">
                          {ranking.metrics.map((metric, i) => (
                            <span key={i} className="px-2 py-1 text-xs rounded-full bg-primary/10 text-primary">
                              {metric}
                            </span>
                          ))}
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <p className="text-sm font-semibold text-primary mb-2 flex items-center gap-1">
                            <CheckCircle className="w-4 h-4" /> Prós
                          </p>
                          <ul className="text-sm text-muted-foreground space-y-1">
                            {ranking.pros.map((pro, i) => (
                              <li key={i}>• {pro}</li>
                            ))}
                          </ul>
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-destructive mb-2 flex items-center gap-1">
                            <XCircle className="w-4 h-4" /> Contras
                          </p>
                          <ul className="text-sm text-muted-foreground space-y-1">
                            {ranking.cons.map((con, i) => (
                              <li key={i}>• {con}</li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>

                <div className="mt-8 p-6 rounded-2xl bg-card border border-accent/30">
                  <div className="flex items-start gap-4">
                    <Lightbulb className="w-8 h-8 text-accent flex-shrink-0" />
                    <div>
                      <h4 className="font-bold text-lg mb-2">Dica de Ouro: Combine Rankings</h4>
                      <p className="text-muted-foreground">
                        Os melhores sistemas usam múltiplos rankings simultaneamente. Por exemplo: um ranking geral 
                        semanal para competição, um ranking de evolução para desenvolvimento pessoal, e desafios 
                        diários de atividades para manter o engajamento constante. Assim, vendedores com diferentes 
                        perfis encontram motivação em pelo menos uma das categorias.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* Exemplos de Desafios */}
            <section className="py-16 sm:py-20" aria-labelledby="challenges-heading">
              <div className="container mx-auto px-4">
                <div className="text-center mb-12">
                  <h2 id="challenges-heading" className="text-3xl sm:text-4xl font-bold mb-4">
                    <span className="bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
                      Exemplos de Desafios e Missões
                    </span>
                  </h2>
                  <p className="text-muted-foreground max-w-2xl mx-auto">
                    Inspire-se com estes exemplos prontos para implementar na sua equipe
                  </p>
                </div>

                <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                  {challengeExamples.map((category, index) => {
                    const Icon = category.icon;
                    return (
                      <Card key={index} className="p-6 border-primary/20 hover:border-primary/50 transition-all">
                        <div className="flex items-center gap-3 mb-4">
                          <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-primary to-accent flex items-center justify-center">
                            <Icon className="w-5 h-5 text-white" aria-hidden="true" />
                          </div>
                          <h3 className="font-bold">{category.category}</h3>
                        </div>
                        <ul className="space-y-3">
                          {category.challenges.map((challenge, i) => (
                            <li key={i} className="flex items-start gap-2 text-sm text-muted-foreground">
                              <ChevronRight className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                              <span>{challenge}</span>
                            </li>
                          ))}
                        </ul>
                      </Card>
                    );
                  })}
                </div>
              </div>
            </section>

            {/* CTA Meio do Artigo */}
            <section className="py-12 sm:py-16" aria-labelledby="cta-mid">
              <div className="container mx-auto px-4">
                <Card className="p-8 sm:p-12 bg-gradient-to-br from-primary via-accent to-secondary border-0 text-center relative overflow-hidden">
                  <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=1920&q=80')] bg-cover bg-center opacity-10" />
                  <div className="absolute inset-0 bg-gradient-to-r from-primary/90 via-accent/90 to-secondary/90" />
                  
                  <div className="relative z-10">
                    <Trophy className="w-14 h-14 text-white mx-auto mb-6 animate-bounce" aria-hidden="true" />
                    <h2 id="cta-mid" className="text-2xl sm:text-4xl font-bold mb-4 text-white">
                      Gamificação + IA = Resultados Extraordinários
                    </h2>
                    <p className="text-lg text-white/90 max-w-2xl mx-auto mb-8">
                      O Vendas Trainer combina gamificação inteligente com treinamento de IA. 
                      Seus vendedores ganham pontos, sobem de nível e competem enquanto aprimoram suas habilidades.
                    </p>
                    <Button
                      size="lg"
                      onClick={() => navigate("/auth")}
                      className="bg-white text-primary hover:bg-white/90 text-lg px-10 py-6 shadow-2xl"
                    >
                      Ver Demo da Gamificação
                      <ArrowRight className="ml-2 w-5 h-5" aria-hidden="true" />
                    </Button>
                  </div>
                </Card>
              </div>
            </section>

            {/* Erros Comuns */}
            <section className="py-16 sm:py-20 bg-muted/30" aria-labelledby="mistakes-heading">
              <div className="container mx-auto px-4">
                <div className="text-center mb-12">
                  <h2 id="mistakes-heading" className="text-3xl sm:text-4xl font-bold mb-4">
                    <span className="bg-gradient-to-r from-destructive to-destructive/70 bg-clip-text text-transparent">
                      6 Erros Fatais na Gamificação
                    </span>
                  </h2>
                  <p className="text-muted-foreground max-w-2xl mx-auto">
                    Evite esses erros que podem destruir seu programa de gamificação
                  </p>
                </div>

                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
                  {commonMistakes.map((item, index) => (
                    <Card key={index} className="p-6 border-destructive/20 hover:border-destructive/40 transition-all">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="w-10 h-10 rounded-full bg-destructive/10 flex items-center justify-center">
                          <XCircle className="w-5 h-5 text-destructive" aria-hidden="true" />
                        </div>
                        <span className="text-sm text-destructive font-medium">Erro #{index + 1}</span>
                      </div>
                      <h3 className="font-bold mb-2">{item.mistake}</h3>
                      <p className="text-sm text-muted-foreground mb-4">
                        <strong className="text-destructive">Consequência:</strong> {item.consequence}
                      </p>
                      <div className="flex items-start gap-2 p-3 rounded-lg bg-primary/5 border border-primary/20">
                        <CheckCircle className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                        <p className="text-sm text-muted-foreground">
                          <strong className="text-foreground">Solução:</strong> {item.solution}
                        </p>
                      </div>
                    </Card>
                  ))}
                </div>
              </div>
            </section>

            {/* Passos para Implementação */}
            <section className="py-16 sm:py-20" aria-labelledby="implementation-heading">
              <div className="container mx-auto px-4">
                <div className="text-center mb-12">
                  <h2 id="implementation-heading" className="text-3xl sm:text-4xl font-bold mb-4">
                    <span className="bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
                      Como Implementar Gamificação: Passo a Passo
                    </span>
                  </h2>
                  <p className="text-muted-foreground max-w-2xl mx-auto">
                    Um guia prático para implementar gamificação na sua equipe
                  </p>
                </div>

                <div className="max-w-4xl mx-auto space-y-8">
                  {implementationSteps.map((step, index) => (
                    <div key={index} className="flex gap-6">
                      <div className="flex-shrink-0">
                        <div className="w-12 h-12 rounded-full bg-gradient-to-br from-primary to-accent flex items-center justify-center text-white font-bold text-xl">
                          {step.step}
                        </div>
                        {index < implementationSteps.length - 1 && (
                          <div className="w-0.5 h-full bg-gradient-to-b from-primary to-transparent mx-auto mt-2" />
                        )}
                      </div>
                      <Card className="flex-1 p-6 border-primary/20">
                        <h3 className="text-xl font-bold mb-2">{step.title}</h3>
                        <p className="text-muted-foreground mb-4">{step.description}</p>
                        <div className="space-y-2">
                          {step.actions.map((action, i) => (
                            <div key={i} className="flex items-center gap-2">
                              <CheckCircle className="w-4 h-4 text-primary flex-shrink-0" />
                              <span className="text-sm text-muted-foreground">{action}</span>
                            </div>
                          ))}
                        </div>
                      </Card>
                    </div>
                  ))}
                </div>
              </div>
            </section>

            {/* Casos de Sucesso */}
            <section className="py-16 sm:py-20 bg-muted/30" aria-labelledby="cases-heading">
              <div className="container mx-auto px-4">
                <div className="text-center mb-12">
                  <h2 id="cases-heading" className="text-3xl sm:text-4xl font-bold mb-4">
                    <span className="bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
                      Casos de Sucesso Reais
                    </span>
                  </h2>
                  <p className="text-muted-foreground max-w-2xl mx-auto">
                    Empresas que transformaram resultados com gamificação
                  </p>
                </div>

                <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
                  {successCases.map((caseStudy, index) => (
                    <Card key={index} className="p-6 border-primary/20 hover:border-primary/50 transition-all">
                      <div className="text-center mb-4">
                        <div className="w-16 h-16 rounded-full bg-gradient-to-br from-primary to-accent flex items-center justify-center mx-auto mb-3">
                          <Trophy className="w-8 h-8 text-white" aria-hidden="true" />
                        </div>
                        <h3 className="text-xl font-bold">{caseStudy.company}</h3>
                        <p className="text-2xl font-bold text-primary">{caseStudy.result}</p>
                      </div>
                      <p className="text-sm text-muted-foreground mb-4">{caseStudy.description}</p>
                      <div className="p-3 rounded-lg bg-accent/10 border border-accent/20">
                        <p className="text-sm">
                          <strong className="text-accent">Lição:</strong>{" "}
                          <span className="text-muted-foreground">{caseStudy.lesson}</span>
                        </p>
                      </div>
                    </Card>
                  ))}
                </div>
              </div>
            </section>

            {/* Métricas para Acompanhar */}
            <section className="py-16 sm:py-20" aria-labelledby="metrics-heading">
              <div className="container mx-auto px-4">
                <div className="text-center mb-12">
                  <h2 id="metrics-heading" className="text-3xl sm:text-4xl font-bold mb-4">
                    <span className="bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
                      Métricas Para Acompanhar o Sucesso
                    </span>
                  </h2>
                  <p className="text-muted-foreground max-w-2xl mx-auto">
                    O que medir para garantir que sua gamificação está funcionando
                  </p>
                </div>

                <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
                  {metricsToTrack.map((category, index) => (
                    <Card key={index} className="p-6 border-primary/20">
                      <h3 className="font-bold text-lg mb-4 text-primary">{category.category}</h3>
                      <ul className="space-y-2">
                        {category.metrics.map((metric, i) => (
                          <li key={i} className="flex items-center gap-2 text-sm text-muted-foreground">
                            <BarChart3 className="w-4 h-4 text-accent flex-shrink-0" />
                            <span>{metric}</span>
                          </li>
                        ))}
                      </ul>
                    </Card>
                  ))}
                </div>
              </div>
            </section>

            {/* Conclusão */}
            <section className="py-16 sm:py-20 bg-muted/30" aria-labelledby="conclusion-heading">
              <div className="container mx-auto px-4">
                <div className="max-w-4xl mx-auto">
                  <h2 id="conclusion-heading" className="text-3xl sm:text-4xl font-bold mb-8 text-center">
                    <span className="bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
                      Conclusão: Gamificação é Estratégia, Não Brincadeira
                    </span>
                  </h2>
                  
                  <div className="prose prose-lg max-w-none">
                    <p className="text-muted-foreground text-lg leading-relaxed mb-6">
                      Gamificação não é sobre transformar o trabalho em um jogo infantil. É sobre aplicar princípios 
                      psicológicos comprovados para criar um ambiente onde vendedores querem dar o seu melhor todos os dias.
                    </p>
                    
                    <p className="text-muted-foreground text-lg leading-relaxed mb-6">
                      Quando bem implementada, a gamificação cria um ciclo virtuoso: vendedores mais engajados 
                      → melhores resultados → mais reconhecimento → ainda mais engajamento. É um motor que, 
                      uma vez ligado, se retroalimenta.
                    </p>

                    <div className="p-6 rounded-2xl bg-card border border-primary/30 my-8">
                      <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
                        <Shield className="w-6 h-6 text-primary" />
                        Lembre-se
                      </h3>
                      <ul className="space-y-2 text-muted-foreground">
                        <li className="flex items-start gap-2">
                          <CheckCircle className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                          <span>Gamificação amplifica comportamentos, não os cria. Tenha processos sólidos primeiro.</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                          <span>Diferentes pessoas são motivadas por coisas diferentes. Ofereça múltiplos caminhos.</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                          <span>Transparência é fundamental. Regras claras criam confiança no sistema.</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                          <span>Itere constantemente. O melhor sistema é aquele que evolui com a equipe.</span>
                        </li>
                      </ul>
                    </div>

                    <p className="text-muted-foreground text-lg leading-relaxed">
                      O futuro das equipes comerciais de alta performance passa pela gamificação. A pergunta não é 
                      mais "se" você vai implementar, mas "quando". E quanto antes começar, mais cedo colherá os resultados.
                    </p>
                  </div>
                </div>
              </div>
            </section>

            {/* Autor */}
            <section className="py-16 sm:py-20" aria-labelledby="author-heading">
              <div className="container mx-auto px-4">
                <div className="max-w-3xl mx-auto">
                  <Card className="p-8 border-primary/30 bg-gradient-to-br from-card to-primary/5">
                    <div className="flex flex-col sm:flex-row gap-6 items-center sm:items-start">
                      <img 
                        src={juniorBianchim}
                        alt="Junior Bianchim - Autor do artigo sobre gamificação em vendas"
                        className="w-32 h-32 rounded-2xl object-cover border-2 border-primary/30"
                      />
                      <div>
                        <span className="text-sm text-primary font-medium">Escrito por</span>
                        <h3 id="author-heading" className="text-2xl font-bold mb-2">Junior Bianchim</h3>
                        <p className="text-muted-foreground mb-4">
                          Especialista em vendas com mais de 20 anos de experiência, incluindo passagem pela TIM Brasil. 
                          Professor de Vendas, Marketing e Administração no SENAC e CEO da Bianchim Business. 
                          Apaixonado por transformar equipes comerciais através de tecnologia e metodologias inovadoras.
                        </p>
                        <div className="flex gap-3">
                          <Button variant="outline" size="sm" onClick={() => navigate("/")}>
                            Mais artigos
                          </Button>
                          <Button size="sm" onClick={() => navigate("/auth")}>
                            Conhecer o Vendas Trainer
                          </Button>
                        </div>
                      </div>
                    </div>
                  </Card>
                </div>
              </div>
            </section>

            {/* Posts Relacionados */}
            <section className="py-16 sm:py-20 bg-muted/30" aria-labelledby="related-heading">
              <div className="container mx-auto px-4">
                <h2 id="related-heading" className="text-2xl sm:text-3xl font-bold mb-8 text-center">
                  Artigos Relacionados
                </h2>
                
                <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
                  <Card 
                    className="p-6 border-primary/20 hover:border-primary/50 transition-all cursor-pointer"
                    onClick={() => navigate("/posts/7-tecnicas-fechamento")}
                  >
                    <div className="flex items-center gap-2 mb-3">
                      <Target className="w-4 h-4 text-primary" />
                      <span className="text-sm text-primary">Técnicas de Vendas</span>
                    </div>
                    <h3 className="font-bold mb-2">7 Técnicas de Fechamento que Todo Vendedor Precisa Dominar</h3>
                    <p className="text-sm text-muted-foreground">Estratégias comprovadas para aumentar sua taxa de conversão</p>
                  </Card>

                  <Card 
                    className="p-6 border-primary/20 hover:border-primary/50 transition-all cursor-pointer"
                    onClick={() => navigate("/posts/ia-treinamento-vendas")}
                  >
                    <div className="flex items-center gap-2 mb-3">
                      <Zap className="w-4 h-4 text-accent" />
                      <span className="text-sm text-accent">Tecnologia</span>
                    </div>
                    <h3 className="font-bold mb-2">Como a IA Está Revolucionando o Treinamento de Vendas</h3>
                    <p className="text-sm text-muted-foreground">O futuro do desenvolvimento de equipes comerciais</p>
                  </Card>

                  <Card 
                    className="p-6 border-primary/20 hover:border-primary/50 transition-all cursor-pointer"
                    onClick={() => navigate("/posts/objecoes-preco")}
                  >
                    <div className="flex items-center gap-2 mb-3">
                      <BookOpen className="w-4 h-4 text-secondary" />
                      <span className="text-sm text-secondary">Negociação</span>
                    </div>
                    <h3 className="font-bold mb-2">Objeções de Preço: Como Transformar em Oportunidade</h3>
                    <p className="text-sm text-muted-foreground">Domine a arte de lidar com objeções de preço</p>
                  </Card>
                </div>
              </div>
            </section>

            {/* CTA Final */}
            <section className="py-16 sm:py-20" aria-labelledby="cta-final">
              <div className="container mx-auto px-4">
                <Card className="p-8 sm:p-12 bg-gradient-to-br from-primary via-accent to-secondary border-0 text-center relative overflow-hidden max-w-4xl mx-auto">
                  <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1552581234-26160f608093?w=1920&q=80')] bg-cover bg-center opacity-10" />
                  <div className="absolute inset-0 bg-gradient-to-r from-primary/90 via-accent/90 to-secondary/90" />
                  
                  <div className="relative z-10">
                    <Gamepad2 className="w-16 h-16 text-white mx-auto mb-6" aria-hidden="true" />
                    <h2 id="cta-final" className="text-2xl sm:text-4xl font-bold mb-4 text-white">
                      Pronto Para Gamificar Sua Equipe?
                    </h2>
                    <p className="text-lg text-white/90 max-w-2xl mx-auto mb-8">
                      O Vendas Trainer já tem gamificação integrada. Seus vendedores ganham pontos, 
                      competem em rankings e sobem de nível enquanto treinam com IA.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-4 justify-center">
                      <Button
                        size="lg"
                        onClick={() => navigate("/auth")}
                        className="bg-white text-primary hover:bg-white/90 text-lg px-10 py-6 shadow-2xl"
                      >
                        Começar Grátis Agora
                        <ArrowRight className="ml-2 w-5 h-5" aria-hidden="true" />
                      </Button>
                    </div>
                    <p className="text-sm text-white/70 mt-6">
                      ✓ Sistema de pontos incluso &nbsp; ✓ Rankings automáticos &nbsp; ✓ Badges e conquistas
                    </p>
                  </div>
                </Card>
              </div>
            </section>
          </article>
        </main>

        <PageNavigation />
        <Footer />
      </div>
    </>
  );
};

export default GamificacaoVendas;